import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, Loader2 } from 'lucide-react';

/**
 * BJ-N5/BJ-N6 — collect the disclosure-safe justification BEFORE privileged
 * access begins.
 *
 * A support operator may not enter a customer's shop under an unstated
 * justification, and what they type here is shown to the affected shop and to
 * anyone whose account they enter. The dialog therefore states that plainly and
 * previews the exact text that will be disclosed.
 *
 * This is deliberately NOT the internal note. `support_sessions.note` is
 * private operator commentary and may carry investigative context; it never
 * reaches a notice.
 */
export interface SupportAccessReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Human label for what is being entered, e.g. the shop name. */
  targetLabel?: string;
  /**
   * Optional starting text. Callers must only pass a generic, disclosure-safe
   * string — never a bug report summary, internal note or customer content.
   */
  defaultReason?: string;
  /** Resolve only after access has actually been granted. */
  onConfirm: (disclosureReason: string) => Promise<void> | void;
}

export const SupportAccessReasonDialog = ({
  open,
  onOpenChange,
  targetLabel,
  defaultReason = '',
  onConfirm,
}: SupportAccessReasonDialogProps) => {
  const [reason, setReason] = useState(defaultReason);
  const [submitting, setSubmitting] = useState(false);

  // Re-seed each time the dialog opens so a previous entry never leaks into a
  // different shop's session.
  useEffect(() => {
    if (open) {
      setReason(defaultReason);
      setSubmitting(false);
    }
  }, [open, defaultReason]);

  const trimmed = reason.trim();
  const canConfirm = trimmed.length > 0 && !submitting;

  const handleConfirm = async () => {
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onConfirm(trimmed);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next); }}>
      <DialogContent data-testid="support-access-reason-dialog">
        <DialogHeader>
          <DialogTitle>Why is this access needed?</DialogTitle>
          <DialogDescription>
            {targetLabel
              ? `You are about to enter ${targetLabel}.`
              : 'You are about to enter a customer shop.'}{' '}
            Support access is recorded and disclosed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Investigating a reported scheduling problem"
            rows={3}
            aria-label="Reason for support access"
            data-testid="support-access-reason-input"
          />

          <Alert>
            <Eye className="h-4 w-4" />
            <AlertDescription data-testid="support-access-reason-preview">
              {trimmed
                ? <>The shop will see: <span className="font-medium">“{trimmed}”</span></>
                : 'This reason is shown to the shop and to anyone whose account you enter. Do not include customer details or private investigation notes.'}
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="support-access-reason-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="support-access-reason-confirm"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enter shop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
